import { PATIENT_MESSAGE_PLACEHOLDER_VALUES } from "@emr/contracts";

/**
 * A calendar file (RFC 5545) for one appointment, made in the browser (PA-9, lead decision L9).
 * It names the clinic, its address and the time only: never the physician, the visit type, the
 * reason, a link, a token or the date of birth, since a calendar is often shared or synced.
 */
export interface CalendarAppointment {
  /** The booking's reference: the event's UID, so a later file updates the same event. */
  readonly bookingReference: string;
  /**
   * The event's revision: 0 at booking, then the booking's version (a manage session's, or a time
   * change's receipt), which only grows, so a calendar keeps the newest file it is given.
   */
  readonly sequence: number;
  readonly startAt: string;
  readonly endAt: string;
  readonly clinicName: string;
  readonly clinicAddress: string;
}

/** The file's name and type when the browser saves it. */
export const CALENDAR_FILE_NAME = "appointment.ics";
export const CALENDAR_FILE_TYPE = "text/calendar;charset=utf-8";

/** A UTC date-time, e.g. 20261020T150000Z. */
function utc(value: string | Date): string {
  return new Date(value)
    .toISOString()
    .replace(/[-:]/g, "")
    .replace(/\.\d{3}Z$/, "Z");
}

/** A TEXT value: backslash, semicolon, comma and line breaks escaped; other controls dropped. */
function text(value: string): string {
  return value
    .replace(/\\/g, "\\\\")
    .replace(/;/g, "\\;")
    .replace(/,/g, "\\,")
    .replace(/\r\n|\r|\n/g, "\\n")
    .replace(/\p{Cc}/gu, "");
}

const encoder = new TextEncoder();

/** Folds a content line at 75 octets, never inside a character; continuations start with a space. */
function fold(line: string): string {
  const parts: string[] = [];
  let current = "";
  let size = 0;
  // A continuation's leading space is one of its 75 octets.
  let limit = 75;
  for (const character of line) {
    const bytes = encoder.encode(character).length;
    if (size + bytes > limit) {
      parts.push(current);
      current = "";
      size = 0;
      limit = 74;
    }
    current += character;
    size += bytes;
  }
  parts.push(current);
  return parts.join("\r\n ");
}

export function appointmentCalendarFile(
  appointment: CalendarAppointment,
  now: Date = new Date(),
): string {
  // A seeded placeholder ("Contact the clinic for its address") is no address, as in messages (PA-8).
  const trimmed = appointment.clinicAddress.trim();
  const address = PATIENT_MESSAGE_PLACEHOLDER_VALUES.has(trimmed) ? "" : trimmed;
  const lines = [
    "BEGIN:VCALENDAR",
    "VERSION:2.0",
    "PRODID:-//Bob Doc//Patient appointment//EN",
    "CALSCALE:GREGORIAN",
    "METHOD:PUBLISH",
    "BEGIN:VEVENT",
    `UID:${text(appointment.bookingReference)}@bobdoc`,
    `SEQUENCE:${Math.max(0, Math.trunc(appointment.sequence))}`,
    `DTSTAMP:${utc(now)}`,
    `DTSTART:${utc(appointment.startAt)}`,
    `DTEND:${utc(appointment.endAt)}`,
    `SUMMARY:${text(`Appointment at ${appointment.clinicName}`)}`,
    ...(address === "" ? [] : [`LOCATION:${text(address)}`]),
    `DESCRIPTION:${text("Contact the clinic to change or cancel this appointment.")}`,
    "TRANSP:OPAQUE",
    "END:VEVENT",
    "END:VCALENDAR",
  ];
  return `${lines.map(fold).join("\r\n")}\r\n`;
}

/** The file as a data: address: the plain fallback where a page cannot make a Blob URL. */
export function calendarDataUrl(file: string): string {
  return `data:${CALENDAR_FILE_TYPE},${encodeURIComponent(file)}`;
}
